import React, { createContext, useCallback, useContext } from 'react'
import { useBoolean, useLocalStorage } from 'react-use'

import { ProjectFavorite, ProjectStatus } from '@makyoapp/makyo-sdk/models/Projects'
import { TeamAdapter } from '@makyoapp/makyo-common'

import { useParamId } from '@/hooks/useParamId'
import { STORAGE_KEYS } from '@/shared/constants'
import { useSearch } from '@/shared/makyo-hooks'

export interface AppFilterValueState {
  team: TeamAdapter
  favorite: string
  plan: string
  status: string
}

export const initialAppFilterValueState: AppFilterValueState = {
  team: {
    id: 'all',
    name: 'All',
  },
  favorite: 'all',
  plan: 'all',
  status: 'all',
}

export interface OrgAppsContextState {
  /**
   * app filter state
   */
  filter: AppFilterValueState
  /**
   * set app filter state
   */
  setFilter: (nextFilter: AppFilterValueState) => void
  /**
   * show add new app modal
   */
  showAdd: boolean
  /**
   * set show add new app modal
   */
  setShowAdd: (nextShow: boolean) => void
  /**
   * current search value (for layout)
   */
  search: string
  /**
   * set search value for app filter
   */
  setSearch: (value: string) => void
  /**
   * clear search
   */
  onClearSearch: () => void
  /**
   * current app filter specific for sdk params
   */
  getAppFilter: () => {
    search: string
    teamID: string
    favorite: ProjectFavorite
    plan: string
    status?: ProjectStatus | ''
  }
  /**
   * reset app filter
   */
  resetFilter: () => void
}

const OrgAppsContext = createContext<OrgAppsContextState>({
  filter: initialAppFilterValueState,
  setFilter: () => null,
  showAdd: false,
  setShowAdd: () => null,
  search: '',
  setSearch: () => {},
  onClearSearch: () => {},
  getAppFilter: () => ({
    search: '',
    teamID: '',
    favorite: ProjectFavorite.All,
    plan: '',
    status: ProjectFavorite.All,
  }),
  resetFilter: () => {},
})

/**
 * use app filter context hook
 */
export function useOrgAppsContext() {
  return useContext(OrgAppsContext)
}

interface AppFilterContextProviderProps {
  children?: React.ReactNode
}

/**
 * AppContext provider
 */
export const AppFilterProvider = ({ children }: AppFilterContextProviderProps) => {
  const teamId = useParamId('team')

  const [showAdd, setShowAdd] = useBoolean(false)

  const [cachedFilter, setCachedFilter] = useLocalStorage(STORAGE_KEYS.PROJECT_FILTER, initialAppFilterValueState, {
    raw: false,
    serializer: (value: AppFilterValueState) => JSON.stringify(value),
    deserializer: (value: string) => JSON.parse(value),
  })

  const [debounceSearch, setDebounceSearch, cancelDebounceSearch, search, setSearch] = useSearch()

  const onClearSearch = () => {
    cancelDebounceSearch()
    setSearch('')
    setDebounceSearch('')
  }

  const filter = cachedFilter || initialAppFilterValueState

  const setFilter = useCallback(
    (newState: AppFilterValueState) => {
      setCachedFilter(newState)
    },
    [setCachedFilter]
  )

  /**
   * Current app filter specific for sdk params, excluding search
   *
   * @see `useGetOrgApps` in `src/resolvers/org`
   */
  const getAppFilter = useCallback(() => {
    let search = debounceSearch
    let teamID = ''
    let favorite = ProjectFavorite.All
    let status = '' as ProjectStatus
    let plan = ''

    const isTeamPage = teamId !== undefined

    if (isTeamPage) {
      teamID = teamId
    } else {
      if (filter.team.id !== 'all') {
        teamID = filter.team.id
      }
    }

    if (filter.favorite !== 'all') {
      favorite = filter.favorite as ProjectFavorite
    }

    if (filter.status !== 'all') {
      status = filter.status as ProjectStatus
    }

    if (filter.plan !== 'all') {
      plan = filter.plan.toLowerCase()
    }

    return { search, teamID, favorite, status, plan }
  }, [filter, teamId, debounceSearch])

  const resetFilter = () => {
    setFilter(initialAppFilterValueState)
    onClearSearch()
  }

  return (
    <>
      <OrgAppsContext.Provider
        value={{
          filter,
          setFilter,
          showAdd,
          setShowAdd,
          getAppFilter,
          resetFilter,
          search,
          setSearch,
          onClearSearch,
        }}
      >
        {children}
      </OrgAppsContext.Provider>
    </>
  )
}
